import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";

const errMsg = (e, f) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : f;
};

export default function Activate() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("This activation link is missing its token."); setLoading(false); return; }
    api.get(`/auth/activation/lookup/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(errMsg(e, "This activation link is invalid or has expired.")))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) return toast.error("Password must be at least 10 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post("/auth/activation/complete", { token, new_password: pw });
      localStorage.setItem("psc_token", r.data.token);
      localStorage.setItem("psc_user", JSON.stringify(r.data.user));
      toast.success("Account activated — welcome to Compliance Hub");
      window.location.href = "/";
    } catch (e2) { toast.error(errMsg(e2, "Could not activate — the link may have expired")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F0E2E] flex items-center justify-center px-4" data-testid="activate-page">
      <div className="w-full max-w-md bg-white border-2 border-slate-200 p-8">
        <div className="flex items-center gap-2 text-[#1E1B4B] mb-1">
          <ShieldCheck size={18} strokeWidth={2.5} />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Compliance Hub</span>
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2">Activate your account</h1>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#1E1B4B]" /></div>
        ) : err ? (
          <div>
            <p className="text-sm text-red-700 bg-red-50 border-2 border-red-200 p-3" data-testid="activate-error">{err}</p>
            <button onClick={() => nav("/login")} className="mt-4 text-sm font-bold text-[#1E1B4B] underline">Go to sign in</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-600">
              Setting a password for <strong>{info?.email}</strong>
              {info?.company_name ? <> at <strong>{info.company_name}</strong></> : null}.
            </p>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">New password</span>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="activate-pw"
                placeholder="Min 10 chars, upper, lower, number"
                className="h-11 w-full px-3 border-2 border-slate-300 focus:border-[#1E1B4B] focus:outline-none text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Confirm password</span>
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="activate-pw2"
                className="h-11 w-full px-3 border-2 border-slate-300 focus:border-[#1E1B4B] focus:outline-none text-sm" />
            </label>
            <button type="submit" disabled={busy} data-testid="activate-submit"
              className="h-11 w-full inline-flex items-center justify-center gap-2 bg-[#14B8A6] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#0D9488] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <><KeyRound size={15} /> Activate & sign in</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
